<template>
    <div class="tables-setup">
        <div class="step-title">{{ $t('get_started.tables_setup_title') }}</div>
        <div class="step-description">{{ $t('get_started.tables_setup_description') }}</div>

        <v-select
            v-model="numberOfSectors"
            class="mb-3"
            :items="[1, 2, 3, 4]"
            :label="$t('get_started.number_of_sectors')"
            hide-details
        />

        <v-row v-for="(sector, index) in sectors" :key="index">
            <v-col cols="8">
                <v-text-field
                    v-model="sector.name"
                    :label="`${$t('general.sector')} ${index + 1} ${$t('general.name').toLowerCase()}`"
                    hide-details
                    class="py-0"
                />
            </v-col>

            <v-col cols="4">
                <v-select
                    v-model="sector.numberOfTables"
                    :items="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]"
                    :label="$t('get_started.number_of_tables')"
                    hide-details
                    class="py-0"
                    @change="createSectorTables(sector)"
                />
            </v-col>
        </v-row>

        <v-slide-y-transition>
            <div
                v-if="tablesErrorMessage == 'mustHaveAtLeastOneSector'"
                class="error--text text-body-2"
            >
                {{ $t('get_started.must_have_at_least_one_sector') }}
            </div>
            <div
                v-if="tablesErrorMessage == 'allSectorsMustBeNamed'"
                class="error--text text-body-2"
            >
                {{ $t('get_started.all_sectors_must_be_named') }}
            </div>
        </v-slide-y-transition>

        <div class="footer">
            <v-btn
                class="flex-1"
                color="primary"
                depressed
                :disabled="busy"
                :loading="busy"
                @click="saveSectors()"
            >
                {{ $t('general.next') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import * as mutationTypes from '@/store/mutationTypes'
import ApiService from '@/services/ApiService'

export default {
    data() {
        return {
            sectors: [],
            numberOfSectors: null,
            tablesErrorMessage: null,
            busy: false,

            sampleSector: {
                name: null,
                numberOfTables: 2,
                tables: [{
                    color: '#FF9800',
                    height: 10,
                    name: 1,
                    pos_left: 45,
                    pos_top: 45,
                    shape: 'rectangular',
                    width: 10
                }]
            },
        };
    },

    watch: {
        numberOfSectors: {
            handler() {
                while (this.numberOfSectors > this.sectors.length) {
                    this.sectors.push(_.cloneDeep(this.sampleSector));
                }
                while (this.numberOfSectors < this.sectors.length) {
                    this.sectors.pop();
                }
            }
        }
    },

    methods: {

        /**
         * Create Sector Tables
         */
        createSectorTables(sector) {
            sector.tables = [];
            for (let i = 1; i <= sector.numberOfTables; i++) {
                sector.tables.push({
                    color: '#FF9800',
                    height: 10,
                    name: i,
                    shape: 'rectangular',
                    width: 10
                });
            }

            if (sector.numberOfTables <= 3) {
                sector.tables.forEach((table, index) => {
                    let spaceBetween = (100 - sector.numberOfTables * 10) / (sector.numberOfTables + 1);

                    table.pos_top = 45;
                    table.pos_left = spaceBetween + index * (10 + spaceBetween);
                });
            } else if (sector.numberOfTables <= 8) {
                sector.tables.forEach((table, index) => {
                    let topCount = Math.ceil(sector.numberOfTables / 2);
                    let bottomCount = Math.floor(sector.numberOfTables / 2);

                    let topSpaceBetween = (100 - topCount * 10) / (topCount + 1);
                    let bottomSpaceBetween = (100 - bottomCount * 10) / (bottomCount + 1);

                    if (index < topCount) {
                        table.pos_top = 80 / 3;
                        table.pos_left = topSpaceBetween + index * (10 + topSpaceBetween);
                    } else {
                        table.pos_top = (80 / 3) * 2 + 10;
                        table.pos_left = bottomSpaceBetween + (index - topCount) * (10 + bottomSpaceBetween);
                    }
                });
            } else if (sector.numberOfTables <= 12) {
                sector.tables.forEach((table, index) => {
                    let botCount = Math.floor(sector.numberOfTables / 3);
                    let topCount = Math.floor((sector.numberOfTables - botCount) / 2);
                    let midCount = sector.numberOfTables - botCount - topCount;

                    let topSpaceBetween = (100 - topCount * 10) / (topCount + 1);
                    let midSpaceBetween = (100 - midCount * 10) / (midCount + 1);
                    let botSpaceBetween = (100 - botCount * 10) / (botCount + 1);

                    if (index < topCount) {
                        table.pos_top = 70 / 4;
                        table.pos_left = topSpaceBetween + index * (10 + topSpaceBetween);
                    } else if (index < topCount + midCount) {
                        table.pos_top = (70 / 4) * 2 + 10;
                        table.pos_left = midSpaceBetween + (index - topCount) * (10 + midSpaceBetween);
                    } else {
                        table.pos_top = (70 / 4) * 3 + 20;
                        table.pos_left = botSpaceBetween + (index - topCount - midCount) * (10 + botSpaceBetween);
                    }
                });
            } else {
                sector.tables.forEach((table, index) => {
                    let botCount = Math.floor(sector.numberOfTables / 4);
                    let topCount = Math.floor((sector.numberOfTables - botCount) / 3);
                    let botMidCount = Math.floor((sector.numberOfTables - botCount - topCount) / 2);
                    let topMidCount = sector.numberOfTables - botCount - topCount - botMidCount;

                    let topSpaceBetween = (100 - topCount * 10) / (topCount + 1);
                    let topMidSpaceBetween = (100 - topMidCount * 10) / (topMidCount + 1);
                    let botMidSpaceBetween = (100 - botMidCount * 10) / (botMidCount + 1);
                    let botSpaceBetween = (100 - botCount * 10) / (botCount + 1);

                    if (index < topCount) {
                        table.pos_top = 60 / 5;
                        table.pos_left = topSpaceBetween + index * (10 + topSpaceBetween);
                    } else if (index < topCount + topMidCount) {
                        table.pos_top = (60 / 5) * 2 + 10;
                        table.pos_left = topMidSpaceBetween + (index - topCount) * (10 + topMidSpaceBetween);
                    } else if (index < topCount + topMidCount + botMidCount) {
                        table.pos_top = (60 / 5) * 3 + 20;
                        table.pos_left =
                            botMidSpaceBetween + (index - topCount - topMidCount) * (10 + botMidSpaceBetween);
                    } else {
                        table.pos_top = (60 / 5) * 4 + 30;
                        table.pos_left =
                            botSpaceBetween + (index - topCount - topMidCount - botMidCount) * (10 + botSpaceBetween);
                    }
                });
            }
        },

        /**
         * Save Sectors
         */
        saveSectors() {
            this.tablesErrorMessage = null;

            this.$nextTick(() => {
                if (!this.numberOfSectors) {
                    this.tablesErrorMessage = 'mustHaveAtLeastOneSector';
                    return false;
                }

                this.sectors.forEach(sector => {
                    if (!sector.name) {
                        this.tablesErrorMessage = 'allSectorsMustBeNamed';
                        return false;
                    }
                });

                if (!this.tablesErrorMessage) {
                    this.busy = true;

                    ApiService.post('/getstarted/sectors', { sectors: this.sectors }).then(response => {
                        this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });
                        this.busy = false;
                        this.$emit('finish')
                    });
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.tables-setup {
    .step-title {
        margin-bottom: 10px;
        font-size: 20px;
    }

    .step-description {
        margin-bottom: 30px;
        font-size: 14px;
        color: #8f9095;
    }

    .footer {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
}
</style>
